import api from './api';

// Enhanced analytics API that connects to live SIS database
export const liveAnalyticsAPI = {
  // Real-time dashboard statistics from live SIS data
  getLiveDashboardStats: () =>
    api.get('/sis-integration/live-dashboard-stats/'),

  // Live school analytics with real data breakdown
  getLiveSchoolAnalytics: (params?: any) =>
    api.get('/sis-integration/live-school-analytics/', { params }),

  // Real enrollment statistics from SIS database
  getLiveEnrollmentStats: (params?: {
    academic_year?: string;
    school_level?: string;
    district?: string;
    gender?: 'male' | 'female' | 'all';
  }) =>
    api.get('/sis-integration/live-enrollment-stats/', { params }),

  // Live staff statistics
  getLiveStaffStats: (params?: {
    academic_year?: string;
    qualification?: string;
    district?: string;
    gender?: 'male' | 'female' | 'all';
  }) =>
    api.get('/sis-integration/live-staff-stats/', { params }),

  // Infrastructure data from actual schools
  getLiveInfrastructureStats: (params?: {
    district?: string;
    school_level?: string;
  }) =>
    api.get('/sis-integration/live-infrastructure-stats/', { params }),

  // Academic performance indicators
  getLiveAcademicPerformance: (params?: {
    academic_year?: string;
    subject?: string;
    school_level?: string;
    district?: string;
  }) =>
    api.get('/sis-integration/live-academic-performance/', { params }),

  // District-wise comparison analytics
  getDistrictComparison: (params?: {
    academic_year?: string;
    metric?: 'enrollment' | 'staff' | 'infrastructure' | 'performance';
  }) =>
    api.get('/sis-integration/district-comparison/', { params }),

  // School-level detailed analytics
  getSchoolLevelAnalytics: (params?: {
    school_level?: 'pre_primary' | 'primary' | 'ordinary_level' | 'advanced_level';
    academic_year?: string;
  }) =>
    api.get('/sis-integration/school-level-analytics/', { params }),

  // Gender-based analytics
  getGenderAnalytics: (params?: {
    academic_year?: string;
    metric?: 'enrollment' | 'staff' | 'performance';
    school_level?: string;
  }) =>
    api.get('/sis-integration/gender-analytics/', { params }),

  // Time-series trends from historical SIS data
  getEnrollmentTrends: (params?: {
    start_year?: string;
    end_year?: string;
    school_level?: string;
    district?: string;
  }) =>
    api.get('/sis-integration/live-enrollment-trends/', { params }),

  // Custom indicator calculations based on live data
  getCustomIndicatorData: (indicatorId: number, params?: {
    academic_year?: string;
    district?: string;
    school_level?: string;
  }) =>
    api.get(`/sis-integration/custom-indicators/${indicatorId}/data/`, { params }),

  // Real-time data quality assessment
  getDataQualityMetrics: () =>
    api.get('/sis-integration/data-quality-metrics/'),

  // Enrollment data quality check
  getEnrollmentDataQuality: (forceRefresh = false) =>
    api.get('/sis-integration/enrollment-data-quality/', {
      params: { refresh: forceRefresh }
    }),

  // Reference data from live database
  getSchoolsList: (params?: {
    region_id?: string;
    district_id?: string;
    school_type?: string;
    level_id?: string;
  }) =>
    api.get('/sis-integration/schools/', { params }),

  getSchoolDetails: (schoolId: string) =>
    api.get(`/sis-integration/schools/${schoolId}/`),

  getRegionsList: () =>
    api.get('/sis-integration/regions/'),

  getDistrictsList: (params?: {
    region_id?: string;
  }) =>
    api.get('/sis-integration/districts/', { params }),

  getSchoolLevelsList: () =>
    api.get('/sis-integration/school-levels/'),

  getShehiasList: (params?: {
    district_id?: string;
  }) =>
    api.get('/sis-integration/shehias/', { params }),

  getReferenceData: () =>
    api.get('/sis-integration/reference-data/'),

  // Export live analytics data
  exportAnalyticsData: (params: {
    format: 'csv' | 'excel' | 'pdf';
    report_type: string;
    filters?: any;
  }) =>
    api.post('/sis-integration/export-analytics/', params, {
      responseType: 'blob'
    }),
};

// Helper functions for data transformation
export const liveDataTransformers = {
  // Transform SIS enrollment data for charts
  transformEnrollmentData: (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data.map(item => ({
      name: item.school_level_name || item.grade_name,
      male: item.male_count || 0,
      female: item.female_count || 0,
      total: item.total_count || 0,
      percentage: item.percentage || 0
    }));
  },

  // Transform district data for geographic visualization
  transformDistrictData: (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data.map(item => ({
      district: item.district_name,
      schools: item.school_count || 0,
      students: item.student_count || 0,
      teachers: item.teacher_count || 0,
      completion_rate: item.completion_rate || 0,
      coordinates: [item.longitude, item.latitude].filter(Boolean)
    }));
  },

  // Transform infrastructure data for dashboard cards
  transformInfrastructureData: (data: any) => {
    return {
      water_access: {
        percentage: Math.round((data.schools_with_water / data.total_schools) * 100),
        with_access: data.schools_with_water,
        without_access: data.schools_without_water,
        total: data.total_schools
      },
      electricity_access: {
        percentage: Math.round((data.schools_with_electricity / data.total_schools) * 100),
        with_access: data.schools_with_electricity,
        without_access: data.schools_without_electricity,
        total: data.total_schools
      },
      internet_access: {
        percentage: Math.round((data.schools_with_internet / data.total_schools) * 100),
        with_access: data.schools_with_internet,
        without_access: data.schools_without_internet,
        total: data.total_schools
      },
      library_access: {
        percentage: Math.round((data.schools_with_library / data.total_schools) * 100),
        with_access: data.schools_with_library,
        without_access: data.schools_without_library,
        total: data.total_schools
      }
    };
  },

  // Transform academic performance data
  transformPerformanceData: (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data.map(item => ({
      subject: item.subject_name,
      average_score: Math.round(item.average_score * 10) / 10,
      pass_rate: Math.round(item.pass_rate * 100) / 100,
      grade_distribution: item.grade_distribution || {},
      total_students: item.total_students || 0
    }));
  },

  // Transform time series data for trend analysis
  transformTrendData: (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data.map(item => ({
      year: item.academic_year,
      month: item.month_name,
      value: item.value,
      change: item.percentage_change || 0,
      baseline: item.baseline_value || 0
    }));
  }
};

export default liveAnalyticsAPI;